import { defaultPartialDayRange, localMidnight, nextAllDayEnd } from './timeOffDateUtils';

export type TimeOffDurationMode = 'allDay' | 'morning' | 'afternoon' | 'custom';

export const WORK_DAY = {
  startHour: 9,
  midHour: 13,
  endHour: 17
} as const;

export const DURATION_MODE_ITEMS: { key: TimeOffDurationMode; text: string }[] = [
  { key: 'allDay', text: 'All day' },
  { key: 'morning', text: 'Morning' },
  { key: 'afternoon', text: 'Afternoon' },
  { key: 'custom', text: 'Custom' }
];

export function durationModeLabel(mode: TimeOffDurationMode): string {
  switch (mode) {
    case 'allDay':
      return 'All-day leave';
    case 'morning':
      return 'Morning half-day';
    case 'afternoon':
      return 'Afternoon half-day';
    default:
      return 'Custom hours';
  }
}

function setLocalTime(day: Date, hour: number, minute = 0): Date {
  const d = new Date(day);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function sameLocalTime(a: Date, b: Date, toleranceMinutes = 1): boolean {
  return Math.abs(a.getTime() - b.getTime()) <= toleranceMinutes * 60 * 1000;
}

function sameLocalDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function applyDurationMode(
  mode: TimeOffDurationMode,
  baseDate: Date
): { startLocal: Date; endLocal: Date; isAllDay: boolean } {
  const day = new Date(baseDate);

  if (mode === 'allDay') {
    const startLocal = localMidnight(day);
    return { startLocal, endLocal: nextAllDayEnd(startLocal), isAllDay: true };
  }

  if (mode === 'morning') {
    return {
      startLocal: setLocalTime(day, WORK_DAY.startHour),
      endLocal: setLocalTime(day, WORK_DAY.midHour),
      isAllDay: false
    };
  }

  if (mode === 'afternoon') {
    return {
      startLocal: setLocalTime(day, WORK_DAY.midHour),
      endLocal: setLocalTime(day, WORK_DAY.endHour),
      isAllDay: false
    };
  }

  const { start, end } = defaultPartialDayRange(day);
  return { startLocal: start, endLocal: end, isAllDay: false };
}

export function inferDurationMode(startLocal: Date, endLocal: Date, isAllDay: boolean): TimeOffDurationMode {
  if (isAllDay) return 'allDay';

  const morning = applyDurationMode('morning', startLocal);
  if (
    sameLocalDate(startLocal, endLocal) &&
    sameLocalTime(startLocal, morning.startLocal) &&
    sameLocalTime(endLocal, morning.endLocal)
  ) {
    return 'morning';
  }

  const afternoon = applyDurationMode('afternoon', startLocal);
  if (
    sameLocalDate(startLocal, endLocal) &&
    sameLocalTime(startLocal, afternoon.startLocal) &&
    sameLocalTime(endLocal, afternoon.endLocal)
  ) {
    return 'afternoon';
  }

  return 'custom';
}

export function applyDurationModeToDraft<T extends { startLocal: Date; endLocal: Date; isAllDay: boolean }>(
  draft: T,
  mode: TimeOffDurationMode,
  baseDate?: Date
): T {
  const applied = applyDurationMode(mode, baseDate ?? draft.startLocal);
  return { ...draft, ...applied };
}
